import { sum, min } from 'lib/array/arrayUtils'
import { mapExperienceCartItemToSummaryViews } from 'checkout/lib/utils/experiences/view'
import { generateExperienceCheckoutTicket } from 'lib/checkout/experiences/cart'
import { isExperienceItem } from 'lib/checkout/checkoutUtils'
import { getExperienceTimesKey } from 'lib/experiences/experienceUtils'
import type { TripChatFlightCartStatus } from 'tripPlanner/api/types'
import type { ExperienceSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import { getCartItemDisplayPrice } from './cartItemPriceSelection'

type CartPanelMappedItineraryCartStatus = TripChatFlightCartStatus
type TripChatExperienceAvailabilityStatus = NonNullable<ExperienceSavedData['experienceAvailabilityStatus']>

export type CartPanelExperienceUnavailableStatus =
  | 'date_unavailable'
  | 'time_unavailable'
  | 'tickets_unavailable'

export type CartPanelItemCartStatus =
  | CartPanelMappedItineraryCartStatus
  | 'pending_hydration'
  | CartPanelExperienceUnavailableStatus

export type CartPanelAccommodationUnavailableStatus =
  | 'selected_rate_unavailable'
  | 'property_sold_out'

const EXPERIENCE_AVAILABILITY_STATUS_MAP: Readonly<Record<TripChatExperienceAvailabilityStatus, CartPanelExperienceUnavailableStatus>> = {
  selected_date_unavailable: 'date_unavailable',
  selected_time_unavailable: 'time_unavailable',
  selected_tickets_unavailable: 'tickets_unavailable',
}

// Keyed on the contract union so a status added to the trip contract fails to
// compile here instead of silently reading as pending hydration.
const KNOWN_ITINERARY_CART_STATUSES: Readonly<Record<TripChatFlightCartStatus, true>> = {
  active: true,
  needs_dates: true,
  needs_time: true,
  duration_unavailable: true,
  selected_rate_unavailable: true,
  property_sold_out: true,
  experience_sold_out: true,
  experience_unavailable: true,
  needs_tickets: true,
  needs_flight_selection: true,
  needs_flight_offer: true,
  not_synced: true,
}

const KNOWN_ITINERARY_CART_STATUS_SET: ReadonlySet<string> = new Set(Object.keys(KNOWN_ITINERARY_CART_STATUSES))

function isItineraryCartStatus(cartStatus: string): cartStatus is TripChatFlightCartStatus {
  return KNOWN_ITINERARY_CART_STATUS_SET.has(cartStatus)
}

export function resolveCartPanelItemCartStatus(
  cartStatus: string | undefined,
): CartPanelItemCartStatus | undefined {
  if (!cartStatus) return undefined
  if (!isItineraryCartStatus(cartStatus)) return undefined
  return cartStatus
}

export function resolveCartPanelExperienceAvailabilityStatus(
  availabilityStatus: ExperienceSavedData['experienceAvailabilityStatus'] | undefined,
): CartPanelExperienceUnavailableStatus | undefined {
  if (!availabilityStatus) return undefined
  return EXPERIENCE_AVAILABILITY_STATUS_MAP[availabilityStatus]
}

export function isCartPanelAccommodationUnavailableStatus(
  cartStatus: CartPanelItemCartStatus,
): cartStatus is CartPanelAccommodationUnavailableStatus {
  return cartStatus === 'selected_rate_unavailable' || cartStatus === 'property_sold_out'
}

function isCartPanelExperienceUnavailableStatus(
  cartStatus: CartPanelItemCartStatus,
): cartStatus is CartPanelExperienceUnavailableStatus {
  return cartStatus === 'date_unavailable' || cartStatus === 'time_unavailable' || cartStatus === 'tickets_unavailable'
}

type CartPanelExperienceOfferUnavailableStatus = 'experience_sold_out' | 'experience_unavailable'

function isCartPanelExperienceOfferUnavailableStatus(
  cartStatus: CartPanelItemCartStatus,
): cartStatus is CartPanelExperienceOfferUnavailableStatus {
  return cartStatus === 'experience_sold_out' || cartStatus === 'experience_unavailable'
}

/** The experience cannot be booked as saved, whether the offer or the chosen slot is gone. */
export function isCartPanelExperienceBookingUnavailableStatus(
  cartStatus: CartPanelItemCartStatus,
): cartStatus is CartPanelExperienceUnavailableStatus | CartPanelExperienceOfferUnavailableStatus {
  return isCartPanelExperienceUnavailableStatus(cartStatus) || isCartPanelExperienceOfferUnavailableStatus(cartStatus)
}

/**
 * 'stale' keeps the last fetched rows after a failed refresh; 'failed' has none.
 * A missing cart is 'loaded': reconciliation recreates it, so its items are
 * being prepared rather than unknown.
 */
export type CartPanelCartQueryStatus = 'signed_out' | 'loading' | 'loaded' | 'stale' | 'failed'

export interface CartPanelCartQueryState {
  isLoading: boolean
  isError: boolean
  isMissingCart: boolean
  hasData: boolean
  /** A retry the customer started after a failed load; react-query reports it as a first load. */
  isRetryingFailedLoad: boolean
}

export function resolveCartPanelCartQueryStatus(
  isLoggedIn: boolean,
  query: CartPanelCartQueryState,
): CartPanelCartQueryStatus {
  if (!isLoggedIn) return 'signed_out'
  if (query.isLoading) return query.isRetryingFailedLoad ? 'failed' : 'loading'
  if (!query.isError || query.isMissingCart) return 'loaded'
  return query.hasData ? 'stale' : 'failed'
}

export function hasCartPanelCartLoadError(
  cartQueryStatus: CartPanelCartQueryStatus,
): cartQueryStatus is Extract<CartPanelCartQueryStatus, 'stale' | 'failed'> {
  return cartQueryStatus === 'failed' || cartQueryStatus === 'stale'
}

export type CartPanelCheckoutPrompt = 'add_dates' | 'preparing'

interface CartPanelHotelStay {
  checkIn: string | undefined
  checkOut: string | undefined
}

// svc-trip needs both dates before it can price or sync a stay.
function hasCartPanelHotelStayDates(stay: CartPanelHotelStay): boolean {
  return !!stay.checkIn && !!stay.checkOut
}

export type CartPanelHotelTileKind = 'stay' | 'needs_dates' | 'duration_unavailable'

export function resolveCartPanelHotelTileKind(
  item: CartPanelHotelStay & { cartStatus: CartPanelItemCartStatus },
): CartPanelHotelTileKind | undefined {
  switch (item.cartStatus) {
    case 'active':
    case 'not_synced':
    case 'selected_rate_unavailable':
    case 'property_sold_out':
      return 'stay'
    case 'needs_dates':
      return 'needs_dates'
    case 'pending_hydration':
      // A dated hotel with no status is still resolving its package, not missing dates.
      return hasCartPanelHotelStayDates(item) ? 'stay' : 'needs_dates'
    case 'duration_unavailable':
      return 'duration_unavailable'
    default:
      return undefined
  }
}

export interface CartPanelCheckoutPromptInput {
  cartStatus: CartPanelItemCartStatus
  cartExcluded: boolean
  cartItem: App.MultiCartItem | undefined
  isAvailable: boolean | undefined
}

export type CartPanelHotelCheckoutPromptInput = CartPanelHotelStay & CartPanelCheckoutPromptInput

/**
 * What a hotel still needs before checkout, shared by every cart surface.
 * 'preparing' covers work svc-trip finishes on its own: syncing a saved stay
 * into the cart, resolving a dated stay's package, or reconciliation restoring
 * a cart row that is missing or no longer matches the itinerary. Undefined
 * means ready, excluded, unavailable (shown by its own message), or unknown
 * because the cart could not be read.
 */
export function resolveCartPanelHotelCheckoutPrompt(
  item: CartPanelHotelCheckoutPromptInput,
  cartQueryStatus: CartPanelCartQueryStatus,
): CartPanelCheckoutPrompt | undefined {
  if (item.cartExcluded || item.isAvailable === false) return undefined
  const canPrepare = cartQueryStatus !== 'signed_out'
  switch (item.cartStatus) {
    case 'needs_dates':
      return 'add_dates'
    case 'pending_hydration':
      if (!hasCartPanelHotelStayDates(item)) return 'add_dates'
      return canPrepare ? 'preparing' : undefined
    case 'not_synced':
      return canPrepare ? 'preparing' : undefined
    case 'active':
      return isAwaitingCartRepair(item, cartQueryStatus) ? 'preparing' : undefined
    default:
      return undefined
  }
}

/** Experiences ask for their own details in the tile, so only svc-trip's preparation is a prompt. */
export function resolveCartPanelExperienceCheckoutPrompt(
  item: CartPanelCheckoutPromptInput,
  cartQueryStatus: CartPanelCartQueryStatus,
): CartPanelCheckoutPrompt | undefined {
  if (item.cartExcluded || item.isAvailable === false || cartQueryStatus === 'signed_out') return undefined
  if (item.cartStatus === 'not_synced') return 'preparing'
  return item.cartStatus === 'active' && isAwaitingCartRepair(item, cartQueryStatus) ? 'preparing' : undefined
}

function isAwaitingCartRepair(
  item: { cartItem: App.MultiCartItem | undefined },
  cartQueryStatus: CartPanelCartQueryStatus,
): boolean {
  return !item.cartItem && cartQueryStatus === 'loaded'
}

interface CartPanelTotalItem {
  cartExcluded: boolean
  price: number | undefined
  memberPrice: number | undefined
  value?: number
  includeInTotal?: boolean
  includeInCount?: boolean
}

export interface CartItemTotalEligibility {
  cartExcluded: boolean
  includeInTotal?: boolean
}

/**
 * Single source of truth for "does this item's price count toward the total the
 * customer is quoted" -- shared by computeCartPanelTotal (below) and the assistant
 * cart-price manifest facts (cartItemAssistantPrices.ts), so a future reader asking
 * the assistant "what's my total" gets an answer reconcilable with what the customer
 * actually sees, rather than the assistant independently re-deriving this condition.
 */
export function countsTowardCartTotal(item: CartItemTotalEligibility): boolean {
  return !item.cartExcluded && item.includeInTotal !== false
}

// 'active' is the only ready state; 'not_synced' and 'pending_hydration' are transient
// system states with no customer action to report, not a blocker worth surfacing to the
// assistant. Every other status names something the customer still needs to do (or an
// availability problem outside their control) before this item can be checked out.
const CART_CHECKOUT_BLOCKER_LABELS: Readonly<Record<CartPanelItemCartStatus, string | undefined>> = {
  active: undefined,
  not_synced: undefined,
  pending_hydration: undefined,
  needs_dates: 'Dates required',
  needs_time: 'Time selection required',
  needs_tickets: 'Ticket selection required',
  needs_flight_selection: 'Flight selection required',
  needs_flight_offer: 'Flight offer required',
  duration_unavailable: 'Selected duration no longer available',
  selected_rate_unavailable: 'Selected rate no longer available',
  property_sold_out: 'Property sold out',
  experience_sold_out: 'Experience sold out',
  experience_unavailable: 'Experience unavailable',
  date_unavailable: 'Selected date unavailable',
  time_unavailable: 'Selected time unavailable',
  tickets_unavailable: 'Selected tickets unavailable',
}

/**
 * A short, human-readable reason this item can't be checked out yet -- undefined when
 * it's already checkout-ready (or in a transient system state with nothing for the
 * customer to act on). Sourced from the same cartStatus the cart panel tiles use to
 * decide which warning banner to show, so the assistant can't flag a different problem
 * than the one actually blocking checkout -- these labels are assistant-facing values in
 * the surface protocol, not customer-facing UI copy, so unlike the tiles' own i18n
 * message strings, they're plain English regardless of locale (matching how every other
 * assistant-facing label in this protocol, e.g. offer page's committed-state labels, is
 * not run through react-intl either).
 */
export function describeCartCheckoutBlocker(cartStatus: CartPanelItemCartStatus): string | undefined {
  return CART_CHECKOUT_BLOCKER_LABELS[cartStatus]
}

/**
 * `useMemberPrice` is intentionally generic, not `hasLuxPlusBenefits` -- callers
 * pass either a real entitlement check (navbar/cart panel totals) or the List
 * view's own preview toggle (footer total), and this function has no way to
 * know or care which. It just picks per the boolean it's given.
 */
export function computeCartPanelTotal(
  items: Array<CartPanelTotalItem>,
  useMemberPrice: boolean,
): { totalPrice: number; totalValue: number; totalItemCount: number } {
  const priceIncludedItems = items.filter(countsTowardCartTotal)
  const countIncludedItems = items.filter(item => !item.cartExcluded && item.includeInCount !== false)
  return {
    totalPrice: sum(priceIncludedItems, item => getCartItemDisplayPrice(item, useMemberPrice) ?? 0),
    totalValue: sum(priceIncludedItems, item => item.value ?? item.price ?? 0),
    totalItemCount: countIncludedItems.length,
  }
}

export interface CartPanelPriceEstimateItem {
  productType: 'accommodation' | 'experience' | 'flight'
  cartStatus: CartPanelItemCartStatus
  includeInTotal: boolean
}

export function shouldIncludeCartPanelItemPriceEstimate(
  item: CartPanelPriceEstimateItem,
): boolean {
  if (item.includeInTotal) return true
  if (item.productType === 'accommodation') return true
  if (item.productType === 'flight') return item.cartStatus !== 'needs_flight_offer'
  return false
}

export interface CartPanelCheckoutReadinessItem {
  source: 'cart' | 'itinerary'
  cartStatus: CartPanelItemCartStatus
  cartExcluded: boolean
  pricing: {
    isAvailable: boolean | undefined
    loading: boolean
  }
  cartItem: App.MultiCartItem | undefined
  // A grouped multi-city itinerary has no single backing cart item; it's ready
  // only when every underlying leg is.
  multiCityLegs?: Array<CartPanelCheckoutReadinessItem>
}

export interface CartPanelLinkedCheckoutProjectionItem extends CartPanelCheckoutReadinessItem {
  productType?: string
  cartItemId: string | undefined
}

export function isCartPanelItemCheckoutReady(item: CartPanelCheckoutReadinessItem): boolean {
  return (
    isCartPanelItemCheckoutCandidate(item) &&
    !item.pricing.loading
  )
}

export function isCartPanelItemCheckoutCandidate(item: CartPanelCheckoutReadinessItem): boolean {
  // A grouped multi-city itinerary is checkout-ready only when every leg is.
  if (item.multiCityLegs && item.multiCityLegs.length > 0) {
    const isCandidate = !item.cartExcluded && item.multiCityLegs.every(isCartPanelItemCheckoutCandidate)
    return isCandidate
  }
  return (
    item.source === 'cart' &&
    item.cartStatus === 'active' &&
    !item.cartExcluded &&
    item.pricing.isAvailable !== false &&
    isMultiCartItemCheckoutActive(item.cartItem)
  )
}

export function isCartPanelItemCheckoutDataLoading(item: CartPanelCheckoutReadinessItem): boolean {
  return item.pricing.loading && isCartPanelItemCheckoutCandidate(item)
}

export function isCartPanelLinkedCheckoutProjectionPending(item: CartPanelLinkedCheckoutProjectionItem): boolean {
  const hasPendingFlightProjection = item.productType === 'flight' &&
    (item.cartStatus === 'needs_flight_selection' || item.cartStatus === 'pending_hydration')
  return (
    item.source === 'itinerary' &&
    !!item.cartItemId &&
    (item.cartStatus === 'active' || hasPendingFlightProjection) &&
    !item.cartExcluded &&
    item.pricing.isAvailable !== false
  )
}

export function isMultiCartItemPurchased(cartItem: App.MultiCartItem | undefined): boolean {
  return !!cartItem?.purchasedBy
}

export function isMultiCartItemCheckoutActive(cartItem: App.MultiCartItem | undefined): boolean {
  return cartItem?.state === 'active' && !isMultiCartItemPurchased(cartItem)
}

// Blockers the customer can resolve from the cart, including a withdrawn rate, which
// they replace by choosing another package. Sold-out and withdrawn offers only allow
// removal, and their tiles already explain why.
const CUSTOMER_FIXABLE_CART_STATUSES: ReadonlySet<CartPanelItemCartStatus> = new Set<CartPanelItemCartStatus>([
  'needs_dates',
  'needs_time',
  'needs_tickets',
  'needs_flight_selection',
  'needs_flight_offer',
  'duration_unavailable',
  'selected_rate_unavailable',
  'date_unavailable',
  'time_unavailable',
  'tickets_unavailable',
])

export interface CartPanelAttentionItem extends CartPanelCheckoutReadinessItem {
  productType: 'accommodation' | 'experience' | 'flight'
  checkoutPrompt?: CartPanelCheckoutPrompt
}

function doesCartPanelItemNeedCustomerAction(item: CartPanelAttentionItem): boolean {
  if (item.cartExcluded || isCartPanelItemCheckoutReady(item)) return false
  if (item.checkoutPrompt) return item.checkoutPrompt === 'add_dates'
  // These statuses are fixed by choosing again, which live pricing reporting the item unavailable does not prevent.
  if (CUSTOMER_FIXABLE_CART_STATUSES.has(item.cartStatus)) return true
  // An unavailable flight can be refreshed or replaced; other unavailable items can only be removed.
  return item.pricing.isAvailable === false && item.productType === 'flight'
}

export interface CartPanelCheckoutSummary {
  readyItemCount: number
  /** Included items that are not ready, whatever the reason. */
  blockedItemCount: number
  /** Blocked items the customer can resolve themselves. */
  attentionItemCount: number
}

export function summariseCartPanelCheckout(items: Array<CartPanelAttentionItem>): CartPanelCheckoutSummary {
  return {
    readyItemCount: items.filter(isCartPanelItemCheckoutReady).length,
    blockedItemCount: items.filter(item => !item.cartExcluded && !isCartPanelItemCheckoutReady(item)).length,
    attentionItemCount: items.filter(doesCartPanelItemNeedCustomerAction).length,
  }
}

export function isExperienceBuyNowBookLaterBookingType(bookingType: string | undefined): boolean {
  return bookingType === 'NO-CALENDAR-FIXED-END' || bookingType === 'NO-CALENDAR-FIXED-VALIDITY'
}

export function isCartPanelExperienceDateRequired(
  bookingType: string | undefined,
  isBuyNowBookLater: boolean,
  isGift: boolean,
): boolean {
  return (
    (bookingType === 'CALENDAR-TIMESLOTS' || bookingType === 'CALENDAR-NO-TIMESLOTS') &&
    !isBuyNowBookLater &&
    !isGift
  )
}

export function isCartPanelExperienceTimeRequired(
  bookingType: string | undefined,
  isBuyNowBookLater: boolean,
  isGift: boolean,
): boolean {
  return bookingType === 'CALENDAR-TIMESLOTS' && !isBuyNowBookLater && !isGift
}

export function getCartPanelExperienceCartTime(
  cartItem: App.MultiCartItem | undefined,
): string | undefined {
  if (cartItem?.type !== 'experience') return undefined
  return cartItem.time
}

export interface CartPanelExperienceStatusInput {
  cartStatus: CartPanelItemCartStatus
  bookingType: string | undefined
  isBuyNowBookLater: boolean
  isGift: boolean
  cartItem: App.MultiCartItem | undefined
}

export function resolveCartPanelExperienceCartStatus({
  cartStatus,
  bookingType,
  isBuyNowBookLater,
  isGift,
  cartItem,
}: CartPanelExperienceStatusInput): CartPanelItemCartStatus {
  if (cartStatus !== 'active') return cartStatus
  if (!isCartPanelExperienceTimeRequired(bookingType, isBuyNowBookLater, isGift)) return cartStatus
  if (!cartItem) return cartStatus

  return getCartPanelExperienceCartTime(cartItem) ? cartStatus : 'needs_time'
}

export interface CartPanelExperienceDisplayStatusInput extends CartPanelExperienceStatusInput {
  hasSelectedTickets: boolean
  isAvailable: boolean | undefined
}

export function resolveCartPanelExperienceDisplayStatus({
  hasSelectedTickets,
  isAvailable,
  ...statusInput
}: CartPanelExperienceDisplayStatusInput): CartPanelItemCartStatus {
  const cartStatus = resolveCartPanelExperienceCartStatus(statusInput)
  if (cartStatus !== 'active') return cartStatus
  if (isAvailable === false && hasSelectedTickets) return 'tickets_unavailable'
  return cartStatus
}

interface ResolvedPrice {
  price: number | undefined
  memberPrice: number | undefined
  value: number | undefined
}

export function resolveItemPrice(
  offer: App.HotelOffer | undefined,
  packageId: string | undefined,
): ResolvedPrice {
  if (!offer || offer.packages.length === 0) {
    return { price: undefined, memberPrice: undefined, value: undefined }
  }

  if (packageId) {
    const matched = offer.packages.find(pkg => pkg.id === packageId)
    if (matched) {
      return {
        price: matched.price,
        memberPrice: matched.memberPrice,
        value: matched.value,
      }
    }
  }

  const cheapest = min(offer.packages, pkg => pkg.price)

  return {
    price: cheapest?.price,
    memberPrice: cheapest?.memberPrice,
    value: cheapest?.value,
  }
}

export interface CartPanelExperienceTicket {
  ticketId: string
  name: string
  count: number
  date?: string
}

export function toCartPanelExperienceTickets(
  tickets: Array<App.Checkout.ExperienceItemTicket>,
): Array<CartPanelExperienceTicket> {
  return tickets
    .filter(ticket => ticket.count > 0)
    .map(ticket => ({
      ticketId: ticket.ticketId,
      name: ticket.name,
      count: ticket.count,
      date: ticket.date,
    }))
}

export function getMultiCartExperienceCheckoutItem(
  cartItem: App.MultiCartExperienceItem | undefined,
): App.Checkout.ExperienceItem | undefined {
  if (!cartItem) return undefined
  return isExperienceItem(cartItem.item) ? cartItem.item : undefined
}

export interface ExperiencePricingInput {
  experience: App.ExperienceOffer | undefined
  timeSlots: Array<App.ExperienceAvailabilityTimeSlot> | undefined
  tickets: Array<CartPanelExperienceTicket>
  date: string | undefined
  time: string | undefined
  isBuyNowBookLater: boolean
  isGift?: boolean
  bookingType?: string
  savedPrice: number | undefined
  savedMemberPrice: number | undefined
  savedValue: number | undefined
  cartPrice: number | undefined
  cartMemberPrice: number | undefined
  loading: boolean
  error: boolean
}

export interface CartPanelExperienceTimesKeyInput {
  experienceId: string
  date?: string
  pickupPointId?: string
  redemptionLocationId?: string
  isBuyNowBookLater: boolean
  isGift?: boolean
  ticketModesMap?: Record<string, string>
}

export function getCartPanelExperienceTimesKey(
  item: CartPanelExperienceTimesKeyInput,
  currency: string,
): string {
  return getExperienceTimesKey(item.experienceId, item.date, {
    currency,
    pickupPointId: item.pickupPointId,
    redemptionLocationId: item.redemptionLocationId,
    isBuyNowBookLater: item.isBuyNowBookLater,
    isGift: item.isGift,
    ticketModesMap: item.ticketModesMap,
  })
}

export interface ExperienceResolvedPricing {
  price: number | undefined
  memberPrice: number | undefined
  value: number | undefined
  isAvailable: boolean | undefined
  loading: boolean
  error: boolean
  hasUnavailableTickets: boolean
}

function toCheckoutExperienceTicket(
  ticket: CartPanelExperienceTicket,
  liveTicket: App.ExperienceItemTicket | undefined,
): App.Checkout.ExperienceItemTicket {
  return {
    ticketId: ticket.ticketId,
    name: ticket.name,
    fareType: liveTicket?.fareType,
    count: ticket.count,
    price: liveTicket?.price ?? 0,
    memberPrice: liveTicket?.memberPrice ?? 0,
    discount: liveTicket?.discount ?? 0,
    date: ticket.date,
  }
}

function findExperienceTicket(
  timeSlots: Array<App.ExperienceAvailabilityTimeSlot>,
  ticket: CartPanelExperienceTicket,
  requiresTime: boolean,
  selectedTime: string | undefined,
): App.ExperienceItemTicket | undefined {
  for (const slot of timeSlots) {
    if (requiresTime && slot.time !== selectedTime) continue
    const matched = slot.tickets.find(liveTicket => (
      (liveTicket.productId === ticket.ticketId || liveTicket.id === ticket.ticketId) &&
      liveTicket.date === ticket.date
    ))
    if (matched) return matched
  }
}

function canResolveSelectedTicketLivePricing({
  bookingType,
  date,
  time,
  isBuyNowBookLater,
  isGift,
}: Pick<ExperiencePricingInput, 'bookingType' | 'date' | 'time' | 'isBuyNowBookLater' | 'isGift'>): boolean {
  // Open-dated tickets reserve no provider date, so they can never match a ticket in a
  // dated availability slot. Attempting live pricing marks every ticket unavailable,
  // which drops the item out of the cart total and offers the customer a ticket
  // reselection they cannot act on. Price these from the cart/saved values instead.
  if (isBuyNowBookLater || isExperienceBuyNowBookLaterBookingType(bookingType)) return false

  const hasRequiredDate = !isCartPanelExperienceDateRequired(bookingType, isBuyNowBookLater, isGift ?? false) || !!date
  const hasRequiredTime = !isCartPanelExperienceTimeRequired(bookingType, isBuyNowBookLater, isGift ?? false) || !!time
  return hasRequiredDate && hasRequiredTime
}

export function prepareCartPanelExperienceCheckoutItem(
  cartItem: App.Checkout.ExperienceItem,
  experience: App.ExperienceOffer | undefined,
  timeSlots: Array<App.ExperienceAvailabilityTimeSlot> | undefined,
): App.Checkout.ExperienceItem {
  if (!experience || !timeSlots) return cartItem

  const requiresTime = experience.bookingType === 'CALENDAR-TIMESLOTS' && !cartItem.isBuyNowBookLater && !cartItem.isGift
  const tickets = cartItem.tickets.map(ticket => {
    const liveTicket = findExperienceTicket(timeSlots, ticket, requiresTime, cartItem.time)
    if (!liveTicket) return ticket

    // TripPlanner usually stores svc-trip ticketId as the upstream productId,
    // while cart-backed tickets may already use the checkout availability id.
    // Translate to checkout ids at this boundary after matching either shape.
    return {
      ...ticket,
      ...generateExperienceCheckoutTicket({ ticket: liveTicket, count: ticket.count }),
    }
  })
  const total = sum(tickets, ticket => ticket.price * ticket.count)
  const memberTotalWithFallback = sum(tickets, ticket => (ticket.memberPrice || ticket.price) * ticket.count)

  return {
    ...cartItem,
    tickets,
    total,
    memberTotal: memberTotalWithFallback < total ? memberTotalWithFallback : 0,
  }
}

export function resolveExperiencePricing({
  experience,
  timeSlots,
  tickets,
  date,
  time,
  isBuyNowBookLater,
  isGift,
  bookingType,
  savedPrice,
  savedMemberPrice,
  savedValue,
  cartPrice,
  cartMemberPrice,
  loading,
  error,
}: ExperiencePricingInput): ExperienceResolvedPricing {
  const resolvedBookingType = experience?.bookingType ?? bookingType
  const canUseLiveTicketPricing = canResolveSelectedTicketLivePricing({
    bookingType: resolvedBookingType,
    date,
    time,
    isBuyNowBookLater,
    isGift,
  })

  if (tickets.length > 0 && experience && timeSlots && canUseLiveTicketPricing) {
    const requiresTime = isCartPanelExperienceTimeRequired(resolvedBookingType, isBuyNowBookLater, isGift ?? false)
    const ticketViews = tickets.map(ticket => {
      const liveTicket = findExperienceTicket(timeSlots, ticket, requiresTime, time)
      return mapExperienceCartItemToSummaryViews(
        toCheckoutExperienceTicket(ticket, liveTicket),
        liveTicket,
      )
    })

    const hasUnavailableTickets = ticketViews.some(ticketView => ticketView?.unavailable)
    const availableTicketViews = ticketViews.filter(ticketView => ticketView && !ticketView.unavailable)

    if (hasUnavailableTickets) {
      return {
        price: undefined,
        memberPrice: undefined,
        value: undefined,
        isAvailable: false,
        loading,
        error,
        hasUnavailableTickets,
      }
    }

    const price = sum(availableTicketViews, ticketView => ticketView?.price ?? 0)
    const memberPriceWithFallback = sum(availableTicketViews, ticketView => ticketView?.memberPrice || ticketView?.price || 0)

    return {
      price,
      memberPrice: memberPriceWithFallback < price ? memberPriceWithFallback : 0,
      value: sum(availableTicketViews, ticketView => ticketView?.value ?? 0),
      isAvailable: true,
      loading,
      error,
      hasUnavailableTickets,
    }
  }

  if (tickets.length > 0) {
    if (loading && !error) {
      const hasCartPrice = cartPrice !== undefined

      return {
        price: hasCartPrice ? cartPrice : savedPrice ?? experience?.price ?? experience?.pricing.price,
        memberPrice: hasCartPrice ? cartMemberPrice : savedMemberPrice ?? experience?.pricing.memberPrice,
        value: hasCartPrice ? undefined : savedValue ?? experience?.pricing.value,
        isAvailable: undefined,
        loading,
        error,
        hasUnavailableTickets: false,
      }
    }

    return {
      price: savedPrice ?? experience?.price ?? experience?.pricing.price,
      memberPrice: savedMemberPrice ?? experience?.pricing.memberPrice,
      value: savedValue ?? experience?.pricing.value,
      isAvailable: experience ? experience.status === 'ONLINE' : undefined,
      loading,
      error,
      hasUnavailableTickets: false,
    }
  }

  return {
    price: experience?.price ?? experience?.pricing.price ?? savedPrice,
    memberPrice: experience?.pricing.memberPrice ?? savedMemberPrice,
    value: experience?.pricing.value ?? savedValue,
    isAvailable: experience ? experience.status === 'ONLINE' : undefined,
    loading,
    error,
    hasUnavailableTickets: false,
  }
}
